"use client";

import Link from "next/link";
import { useFormStatus } from "react-dom";
import { useSearchParams } from "next/navigation";
import { requestPasswordReset } from "@/app/login/actions";

const CTA_GREEN = "#0dfe00";

function Spinner() {
  return (
    <svg
      className="h-4 w-4 animate-spin"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
        fill="none"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z"
      />
    </svg>
  );
}

function SubmitButton() {
  const { pending } = useFormStatus();

  return (
    <button
      type="submit"
      aria-disabled={pending}
      disabled={pending}
      className="flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-neutral-950 shadow-sm transition hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-white/30 disabled:opacity-70 disabled:cursor-not-allowed"
      style={{ backgroundColor: CTA_GREEN }}
    >
      {pending ? (
        <>
          <Spinner />
          <span>Sending…</span>
        </>
      ) : (
        "Send reset link"
      )}
    </button>
  );
}


function AuthShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(1000px_500px_at_15%_10%,rgba(13,254,0,0.14),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(900px_450px_at_85%_20%,rgba(255,255,255,0.08),transparent_60%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(255,255,255,0.04),transparent_25%,transparent_75%,rgba(255,255,255,0.04))]" />
      </div>

      <div className="mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-12 sm:px-6">
        <div className="w-full max-w-md">
          <Link href="/login" className="text-sm text-neutral-400 hover:text-white">
            ← Back to sign in
          </Link>

          <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-6">
            <h1 className="text-2xl font-semibold tracking-tight text-neutral-200">
              {title}
            </h1>
            {subtitle ? (
              <p className="mt-2 text-sm text-neutral-400">{subtitle}</p>
            ) : null}

            <div className="mt-5">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default async function ForgotPasswordPage({
  searchParams,
}: {
  searchParams?: Promise<{ sent?: string }>;
}) {
  const sp = (await searchParams) ?? {};
  const sent = sp.sent === "1";

  return (
    <AuthShell
      title="Reset your password"
      subtitle="Enter your email and we’ll send you a reset link."
    >
      {sent ? (
        <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/10 p-4 text-sm text-emerald-100">
          Email sent. If an account exists for that address, you’ll receive a password reset link shortly.
          Please check your inbox (and spam).
        </div>
      ) : null}

      {/* Using action= instead of formAction gives useFormStatus() the right pending signal */}
      <form action={requestPasswordReset} className="mt-4 space-y-3">
        <input
          name="email"
          type="email"
          placeholder="Email"
          className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-neutral-200 placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-white/20"
          required
          autoComplete="email"
        />

        <SubmitButton />

        <div className="text-center">
          <Link
            href="/signup"
            className="text-sm font-semibold text-neutral-300 hover:text-white"
          >
            Need an account? Sign up
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}
